/**
 * Retrieves all unique disciplines from the list of vacancies.
 *
 * @param {Array} vacancies - List of vacancies, each with a 'disciplines' array
 * @returns {Array} Array of unique discipline names
 */
export function getUniqueDisciplines(vacancies) {
    return [...new Set(vacancies.flatMap(v => v.disciplines || []))];
}

/**
 * Retrieves all unique brands from the list of vacancies.
 *
 * @param {Array} vacancies - List of vacancies, each with a 'brands' array
 * @returns {Array} Array of unique brand names
 */
export function getUniqueBrands(vacancies) {
    return [...new Set(vacancies.flatMap(v => v.brands || []))];
}


/**
 * Retrieves all unique locations from the list of vacancies.
 *
 * @param {Array} vacancies - List of vacancies, each with a 'location' string
 * @returns {Array} Array of unique location names
 */
export function getUniqueLocations(vacancies) {
    return [...new Set(vacancies.map(v => v.location).filter(Boolean))];
}

/**
 * Generates a list of working hour options (in 4-hour steps)
 * based on the working hours that actually appear in the vacancies.
 *
 * @param {Array} vacancies - List of vacancies with working hour info
 * @returns {Array} Array of objects with value and label, suitable for dropdowns or checkboxes
 */
export function getWorkingHourOptions(vacancies) {
    const allHours = new Set();

    vacancies.forEach(value => {
        // Only include if both values actually exist (not null/undefined)
        const min = typeof value.working_hours_min === 'number' ? value.working_hours_min : null;
        const max = typeof value.working_hours_max === 'number' ? value.working_hours_max : null;

        // Skip vacancies with invalid or missing ranges
        if (min === null || max === null || max < min) return;

        for (let h = min; h <= max; h += 4) {
            // Only include values greater than 0,
            // unless explicitly defined as 0 in the data
            if (h > 0 || (h === 0 && (value.working_hours_min === 0 || value.working_hours_max === 0))) {
                allHours.add(h);
            }
        }
    });

    return Array.from(allHours)
        .sort((a, b) => a - b)
        .map(hour => ({
            value: hour,
            label: `${hour} uur`
        }));
}


/**
 * Retrieves all unique years of experience from the list of vacancies.
 *
 * @param {Array} vacancies - List of vacancies with a numeric 'experience' field
 * @returns {Array} Sorted list of objects with value and label
 */
export function getUniqueExperiences(vacancies) {
    const experienceValues = vacancies
        .map(v => v.experience)
        .filter(exp => typeof exp === 'number');

    return [...new Set(experienceValues)]
        .sort((a, b) => a - b)
        .map(exp => ({
            value: exp,
            label: `${exp}+ jaar werkervaring`
        }));
}

export function buildAllFilterOptions(vacancies) {
    return {
        disciplines: getUniqueDisciplines(vacancies),
        brands: getUniqueBrands(vacancies),
        locations: getUniqueLocations(vacancies),
        workingHours: getWorkingHourOptions(vacancies),
        experiences: getUniqueExperiences(vacancies),
    };
}
